import {
  ConnectionState,
  LocalTrack,
  LocalTrackPublication,
  Participant,
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
  TrackPublication,
} from 'livekit-client';
import type { LiveKitCredentials } from '../types/calls';

export type DirectSfuConnectionState = 'connected' | 'reconnecting' | 'disconnected';

interface DirectSfuMediaOptions {
  /** Relay-only ICE, same preference P2P and group calls honour. */
  protectIpAddress: boolean;
  /** The 1:1 provider's hidden <audio> sink, shared with the P2P path. */
  audioElement: HTMLAudioElement | null;
  outputDeviceId: string | null;
}

/**
 * Media for a 1:1 call the server routed through LiveKit ("direct SFU") so it
 * can be recorded. It is the P2P PeerService's counterpart for CallContext:
 * the 1:1 lifecycle, UI and call logging stay where they are, and only the
 * transport changes. Group calls keep their own room wiring in
 * GroupCallContext (tiles, speakers, add-participant), which this does not
 * need. There is exactly one remote participant: the peer.
 *
 * Video is opt-in on top of the audio call: either side may publish its
 * camera (the mobile app does with `setCameraEnabled`), and the peer's camera
 * is reported separately from its audio so it never decides "connected".
 */
export class DirectSfuMedia {
  /** The peer's audio arrived (also after the peer reconnects). */
  onRemoteStream: ((stream: MediaStream | null) => void) | null = null;
  /** The peer left the room (crash, network loss, or hang-up). */
  onPeerLeft: (() => void) | null = null;
  /** This side's own room connection changed after the initial connect. */
  onConnectionStateChange: ((state: DirectSfuConnectionState) => void) | null = null;
  /**
   * The peer's camera to show, or null when there is none to show (not
   * published, turned off, unsubscribed, or the peer left). A peer that
   * rejoins reports its new track.
   */
  onRemoteVideo: ((track: RemoteTrack | null) => void) | null = null;
  /** This side's own live camera (self-view), or null while it is off. */
  onLocalVideo: ((track: LocalTrack | null) => void) | null = null;
  /** The peer's shared screen, or null when they are not sharing. */
  onRemoteScreen: ((track: RemoteTrack | null) => void) | null = null;
  /** This side is sharing its screen (also false once the browser's own Stop ended it). */
  onLocalScreen: ((sharing: boolean) => void) | null = null;

  private readonly room: Room;
  private closed = false;
  // Connection changes are reported only once the first connect finished:
  // the initial Connected is the caller's cue to post `join`, not a recovery.
  private ready = false;
  private muted = false;
  private remoteTrack: RemoteTrack | null = null;
  // The peer's subscribed camera, kept while muted so unmuting shows it again.
  private remoteVideoTrack: RemoteTrack | null = null;
  private remoteScreenTrack: RemoteTrack | null = null;

  constructor(private readonly options: DirectSfuMediaOptions) {
    this.room = new Room({ adaptiveStream: true, dynacast: true });
    this.room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        this.attach(track);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (this.remoteTrack === track) this.detach();
        if (this.remoteVideoTrack === track) this.clearRemoteVideo();
        if (this.remoteScreenTrack === track) this.clearRemoteScreen();
      })
      // A camera turned off is muted, not unpublished: back to the avatar
      // until it is turned on again.
      .on(RoomEvent.TrackMuted, (publication: TrackPublication, participant: Participant) => {
        this.onTrackMuteChanged(publication, participant);
      })
      .on(RoomEvent.TrackUnmuted, (publication: TrackPublication, participant: Participant) => {
        this.onTrackMuteChanged(publication, participant);
      })
      .on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication) => {
        if (publication.source === Track.Source.Camera) this.reportLocalVideo();
        // Includes the browser's own "Stop sharing": the track ends and
        // livekit-client unpublishes it.
        if (publication.source === Track.Source.ScreenShare) this.reportLocalScreen();
      })
      .on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication) => {
        if (publication.source === Track.Source.ScreenShare) this.reportLocalScreen();
      })
      .on(RoomEvent.ParticipantDisconnected, () => {
        if (this.closed) return;
        this.detach();
        this.clearRemoteVideo();
        this.clearRemoteScreen();
        this.onPeerLeft?.();
      })
      .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        if (this.closed || !this.ready) return;
        if (state === ConnectionState.Reconnecting) this.onConnectionStateChange?.('reconnecting');
        else if (state === ConnectionState.Connected) this.onConnectionStateChange?.('connected');
      })
      .on(RoomEvent.Disconnected, () => {
        if (this.closed || !this.ready) return;
        this.onConnectionStateChange?.('disconnected');
      });
  }

  /**
   * Join the call's room and publish the microphone. Throws on any failure.
   * `iceServers` are the app's own STUN/TURN (`/calls/ice-servers`): the SFU
   * offers no relay of its own, so relay-only ("protect IP") needs them.
   */
  async connect(credentials: LiveKitCredentials, muted: boolean, iceServers?: RTCIceServer[]): Promise<void> {
    if (!credentials.serverUrl || !credentials.participantToken) {
      throw new Error('Invalid LiveKit credentials');
    }
    this.muted = muted;
    // rtcConfig is a connect option in livekit-client (a Room option is
    // silently dropped).
    await this.room.connect(credentials.serverUrl, credentials.participantToken, {
      rtcConfig: {
        iceTransportPolicy: this.options.protectIpAddress ? 'relay' : 'all',
        ...(iceServers?.length ? { iceServers } : {}),
      },
    });
    if (this.closed) throw new Error('Direct call closed while connecting');
    // Reads the latest value: a mute toggled while connecting is kept.
    await this.room.localParticipant.setMicrophoneEnabled(!this.muted);
    this.ready = true;
    // A peer already in the room (the caller, for the callee) published
    // before this side subscribed to events for it.
    this.room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.track) this.attach(publication.track as RemoteTrack);
      });
    });
  }

  setMuted(muted: boolean): Promise<void> {
    this.muted = muted;
    // Before the room is up, connect() applies the stored value.
    if (this.closed || !this.ready) return Promise.resolve();
    return this.room.localParticipant.setMicrophoneEnabled(!muted).then(() => undefined);
  }

  /**
   * Turn this side's camera on or off; audio keeps publishing either way.
   * Off mutes the camera publication, which also stops the capture track so
   * the OS camera light goes out. Rejects with the getUserMedia error when
   * the camera cannot start (denied, missing, busy); the self-view then
   * reports off.
   */
  async setCameraEnabled(enabled: boolean): Promise<void> {
    if (this.closed || !this.ready) throw new Error('Direct call is not connected');
    try {
      const publication = await this.room.localParticipant.setCameraEnabled(enabled);
      if (this.closed) {
        // Hung up while the camera was starting: close() already ran, so
        // release this capture here or the camera stays lit.
        publication?.track?.stop();
        return;
      }
    } finally {
      if (!this.closed) this.reportLocalVideo();
    }
  }

  /**
   * Share this side's screen (the browser asks which screen, window or tab)
   * or stop. Rejects with the getDisplayMedia error when it does not start;
   * a refusal (NotAllowedError) changes nothing.
   */
  async setScreenShareEnabled(enabled: boolean): Promise<void> {
    if (this.closed || !this.ready) throw new Error('Direct call is not connected');
    try {
      await this.room.localParticipant.setScreenShareEnabled(enabled, { audio: false });
    } finally {
      if (!this.closed) this.reportLocalScreen();
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.detach();
    this.remoteVideoTrack = null;
    this.remoteScreenTrack = null;
    this.room.removeAllListeners();
    // Every local capture, the camera included: disconnect(true) unpublishes
    // too, but stopping here is synchronous.
    this.room.localParticipant.trackPublications.forEach((publication) => publication.track?.stop());
    void this.room.disconnect(true).catch(() => { /* local teardown is best-effort */ });
  }

  private attach(track: RemoteTrack): void {
    if (this.closed) return;
    if (track.kind === Track.Kind.Video) {
      this.attachVideo(track);
      return;
    }
    if (track.kind !== Track.Kind.Audio) return;
    if (this.remoteTrack && this.remoteTrack !== track) this.detach();
    this.remoteTrack = track;
    const element = this.options.audioElement;
    if (element) {
      // attach() sets the stream and starts playback (the sink is autoPlay).
      track.attach(element);
      const sinkId = this.options.outputDeviceId;
      const setSinkId = (element as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId;
      if (sinkId && typeof setSinkId === 'function') {
        void setSinkId.call(element, sinkId).catch(() => { /* default speaker */ });
      }
    }
    const stream = typeof MediaStream !== 'undefined' && track.mediaStreamTrack
      ? new MediaStream([track.mediaStreamTrack])
      : null;
    this.onRemoteStream?.(stream);
  }

  private attachVideo(track: RemoteTrack): void {
    if (track.source === Track.Source.ScreenShare) {
      this.remoteScreenTrack = track;
      this.onRemoteScreen?.(track);
      return;
    }
    if (track.source !== Track.Source.Camera) return;
    this.remoteVideoTrack = track;
    this.onRemoteVideo?.(track.isMuted ? null : track);
  }

  private clearRemoteVideo(): void {
    if (!this.remoteVideoTrack) return;
    this.remoteVideoTrack = null;
    // The <video> that showed it detaches itself when it unmounts.
    if (!this.closed) this.onRemoteVideo?.(null);
  }

  private clearRemoteScreen(): void {
    if (!this.remoteScreenTrack) return;
    this.remoteScreenTrack = null;
    if (!this.closed) this.onRemoteScreen?.(null);
  }

  private reportLocalScreen(): void {
    if (this.closed) return;
    this.onLocalScreen?.(this.room.localParticipant.isScreenShareEnabled);
  }

  private onTrackMuteChanged(publication: TrackPublication, participant: Participant): void {
    if (this.closed || publication.source !== Track.Source.Camera) return;
    if (participant === this.room.localParticipant) {
      // The camera stopped on its own (unplugged, or taken by the OS).
      this.reportLocalVideo();
      return;
    }
    const track = this.remoteVideoTrack;
    if (!track || publication.track !== track) return;
    this.onRemoteVideo?.(publication.isMuted ? null : track);
  }

  /** Report this side's camera as it actually is, not as last requested. */
  private reportLocalVideo(): void {
    if (this.closed) return;
    const publication = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
    const live = publication && !publication.isMuted ? publication.track ?? null : null;
    this.onLocalVideo?.(live);
  }

  private detach(): void {
    const track = this.remoteTrack;
    this.remoteTrack = null;
    if (!track) return;
    const element = this.options.audioElement;
    try {
      if (element) track.detach(element);
      else track.detach();
    } catch { /* already detached */ }
  }
}
